import { Outlet, Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Instagram Icon
const InstagramIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// Facebook Icon
const FacebookIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Minimalist Magnolia Logo Component
const MagnoliaLogo = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    {/* Outer petals - sage green outlines */}
    <path d="M20 4C20 4 24 10 24 16C24 18 22 20 20 20C18 20 16 18 16 16C16 10 20 4 20 4Z" 
          stroke="#5B7B5B" strokeWidth="1.5" fill="none"/>
    <path d="M36 20C36 20 30 24 24 24C22 24 20 22 20 20C20 18 22 16 24 16C30 16 36 20 36 20Z" 
          stroke="#5B7B5B" strokeWidth="1.5" fill="none"/>
    <path d="M20 36C20 36 16 30 16 24C16 22 18 20 20 20C22 20 24 22 24 24C24 30 20 36 20 36Z" 
          stroke="#5B7B5B" strokeWidth="1.5" fill="none"/>
    <path d="M4 20C4 20 10 16 16 16C18 16 20 18 20 20C20 22 18 24 16 24C10 24 4 20 4 20Z" 
          stroke="#5B7B5B" strokeWidth="1.5" fill="none"/>
    {/* Diagonal petals */}
    <path d="M30 8C30 8 28 14 24 18C22.5 19.5 20.5 20 20 20C19.5 20 20.5 18.5 22 17C26 13 30 8 30 8Z" 
          stroke="#5B7B5B" strokeWidth="1.2" fill="none"/>
    <path d="M32 30C32 30 26 28 22 24C20.5 22.5 20 20.5 20 20C20 19.5 21.5 20.5 23 22C27 26 32 30 32 30Z" 
          stroke="#5B7B5B" strokeWidth="1.2" fill="none"/>
    <path d="M10 32C10 32 12 26 16 22C17.5 20.5 19.5 20 20 20C20.5 20 19.5 21.5 18 23C14 27 10 32 10 32Z" 
          stroke="#5B7B5B" strokeWidth="1.2" fill="none"/>
    <path d="M8 10C8 10 14 12 18 16C19.5 17.5 20 19.5 20 20C20 20.5 18.5 19.5 17 18C13 14 8 10 8 10Z" 
          stroke="#5B7B5B" strokeWidth="1.2" fill="none"/>
    {/* Center - tiny magenta */}
    <circle cx="20" cy="20" r="3" fill="#C41E7F"/>
  </svg>
);

// Cities served - Palm Beach County
const CITIES_SERVED = [
  "Lake Worth", "West Palm Beach", "Boca Raton", "Boynton Beach", 
  "Delray Beach", "Wellington", "Jupiter", "Palm Beach Gardens", 
  "Greenacres", "Royal Palm Beach", "Riviera Beach", "Lantana", "Palm Springs"
];

export const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const [socialLinks, setSocialLinks] = useState({
    instagram_url: "https://www.instagram.com/magnolia.eventsrental",
    facebook_url: "https://www.facebook.com/share/1AcmPpFtMc/?mibextid=wwXIfr",
    tiktok_url: null,
    tiktok_enabled: false
  });

  // Fetch social links from database
  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await axios.get(`${API}/settings/social`);
        setSocialLinks(response.data);
      } catch (error) {
        console.error("Failed to load social links");
      }
    };
    fetchSocialLinks();
    // Refresh every 60 seconds
    const interval = setInterval(fetchSocialLinks, 60000);
    return () => clearInterval(interval);
  }, []);

  const navLinks = [
    { path: "/", label: t.nav.home },
    { path: "/products", label: t.nav.products },
    { path: "/booking", label: t.nav.booking },
    { path: "/contact", label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-sage/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
              <MagnoliaLogo className="w-10 h-10 group-hover:scale-110 transition-transform" />
              <span className="font-heading font-bold text-xl text-charcoal">
                Magnolia Events and Rentals LLC
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                  className={`font-medium transition-colors ${
                    isActive(link.path)
                      ? "text-sage"
                      : "text-charcoal/70 hover:text-sage"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Social Icons in Header */}
              <div className="flex items-center gap-2 border-l border-charcoal/10 pl-4 ml-2">
                {socialLinks.instagram_url && (
                  <a
                    href={socialLinks.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Follow Magnolia Events on Instagram"
                    className="p-2 text-charcoal/60 hover:text-pink-600 transition-colors"
                    data-testid="header-instagram"
                  >
                    <InstagramIcon className="w-5 h-5" />
                  </a>
                )}
                {socialLinks.facebook_url && (
                  <a
                    href={socialLinks.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Follow Magnolia Events on Facebook"
                    className="p-2 text-charcoal/60 hover:text-blue-600 transition-colors"
                    data-testid="header-facebook"
                  >
                    <FacebookIcon className="w-5 h-5" />
                  </a>
                )}
                {socialLinks.tiktok_enabled && socialLinks.tiktok_url && (
                  <a
                    href={socialLinks.tiktok_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Follow Magnolia Events on TikTok"
                    className="p-2 text-charcoal/60 hover:text-black transition-colors"
                    data-testid="header-tiktok"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                )}
              </div>
              
              <Link to="/booking">
                <Button
                  data-testid="nav-book-now-btn"
                  className="bg-sage hover:bg-sage-dark text-white rounded-full px-6 btn-hover"
                >
                  {t.nav.booking}
                </Button>
              </Link>
              
              {/* Language Toggle */}
              <button
                onClick={toggleLanguage}
                data-testid="language-toggle"
                className="flex items-center gap-2 px-3 py-2 rounded-full border border-sage/30 text-charcoal hover:bg-sage/10 transition-colors font-medium text-sm"
              >
                <Globe className="w-4 h-4" />
                {language === "en" ? "Español" : "English"}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2">
              {/* Mobile Social Icons */}
              {socialLinks.instagram_url && (
                <a
                  href={socialLinks.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Follow Magnolia Events on Instagram"
                  className="p-1.5 text-charcoal/60"
                >
                  <InstagramIcon className="w-4 h-4" />
                </a>
              )}
              {/* Mobile Language Toggle */}
              <button
                onClick={toggleLanguage}
                data-testid="mobile-language-toggle"
                className="flex items-center gap-1 px-2 py-1.5 rounded-full border border-sage/30 text-charcoal text-sm"
              >
                <Globe className="w-4 h-4" />
                {language === "en" ? "ES" : "EN"}
              </button>
              <button
                data-testid="mobile-menu-btn"
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-charcoal" />
                ) : (
                  <Menu className="w-6 h-6 text-charcoal" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-sage/10">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`font-medium py-2 ${
                      isActive(link.path)
                        ? "text-sage"
                        : "text-charcoal/70"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link to="/booking" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-sage hover:bg-sage-dark text-white rounded-full">
                    {t.nav.booking}
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-charcoal text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <MagnoliaLogo className="w-10 h-10" />
                <span className="font-heading font-bold text-xl">
                  Magnolia Events and Rentals LLC
                </span>
              </div>
              <p className="text-white/60 max-w-md mb-6">
                {t.footer.tagline}
              </p>
              
              {/* Social Media Icons in Footer */}
              <div className="flex items-center gap-4 flex-wrap">
                {socialLinks.instagram_url && (
                  <a
                    href={socialLinks.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Follow Magnolia Events on Instagram"
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-pink-600/20 rounded-full transition-colors group"
                    data-testid="footer-instagram"
                  >
                    <InstagramIcon className="w-5 h-5 group-hover:text-pink-400" />
                    <span className="text-sm font-medium">Instagram</span>
                  </a>
                )}
                {socialLinks.facebook_url && (
                  <a
                    href={socialLinks.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Follow Magnolia Events on Facebook"
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-blue-600/20 rounded-full transition-colors group"
                    data-testid="footer-facebook"
                  >
                    <FacebookIcon className="w-5 h-5 group-hover:text-blue-400" />
                    <span className="text-sm font-medium">Facebook</span>
                  </a>
                )}
                {socialLinks.tiktok_enabled && socialLinks.tiktok_url && (
                  <a
                    href={socialLinks.tiktok_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Follow Magnolia Events on TikTok"
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors group"
                    data-testid="footer-tiktok"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    <span className="text-sm font-medium">TikTok</span>
                  </a>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-bold mb-4">{t.footer.quickLinks}</h4>
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-bold mb-4">{t.footer.contact}</h4>
              <div className="flex flex-col gap-2 text-white/60">
                <p>info@magnoliaeventsandrentals.com</p>
                <p>(561) 501-1241</p>
                <p className="text-white/40 text-xs mt-2 italic">
                  Office Location Only - All bookings handled online or by phone. No equipment or showroom at this address.
                </p>
              </div>
            </div>
          </div>

          {/* City-Net Footer - Serving Palm Beach County */}
          <div className="border-t border-white/10 mt-12 pt-8">
            <h4 className="font-heading font-bold text-lg mb-4">
              {language === "es" ? "Sirviendo todo el Condado de Palm Beach" : "Serving All of Palm Beach County"}
            </h4>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {CITIES_SERVED.map((city, index) => (
                <span key={city} className="text-white/50 text-sm">
                  {city}{index < CITIES_SERVED.length - 1 && <span className="ml-4 text-white/20">•</span>}
                </span>
              ))}
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} Magnolia Events and Rentals LLC. {t.footer.rights}
            </p>
            <Link
              to="/admin"
              className="text-white/40 text-sm hover:text-white/60 transition-colors"
              data-testid="admin-link"
            >
              Admin Portal
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
